import { FC, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { AtendimentoDtoCid, AtendimentoView } from '../../../atendimento/core/_models'
import { adicionarCid, deleteCid } from '../../../atendimento/core/_requests'
import { updateAtendimento } from '../../atendimentos-medico-list/core/_requests'
import {
  ButtonType,
  OptionsHelper,
  situacaoDiagnosticoLabels,
  SituacaoDiagnosticoType,
  showError,
} from '../../../../../../_metronic/helpers'
import {
  Button,
  FormSelect,
  IOption,
  ItemList,
  Label,
  LoadingIndicator,
} from '../../../../../components'
import { CIDSchema } from './validation/CIDSchema'
import { useT } from '_metronic/i18n/Metronici18n'
import styles from './CIDModal.module.css'

type DiagnosticoFormProps = {
  atendimento: AtendimentoView | undefined
  /** Recarrega o atendimento depois de cada gravação (CID incluído/removido, situação salva). */
  onAtualizar: () => void
  /**
   * Fecha o modal. Sem ele o formulário é uma seção da tela (parecer da interconsulta): não há
   * Cancelar, a situação grava na escolha e o botão só adiciona CID.
   */
  onFechar?: () => void
  /**
   * Situação escolhida na tela (gravada ou não). Serve a quem precisa saber se o que está à vista
   * bate com o que foi gravado, como o parecer da interconsulta antes de finalizar.
   */
  onSituacaoChange?: (situacao: SituacaoDiagnosticoType | undefined) => void
}

/** Situação diagnóstica e CIDs do atendimento; cada ação grava na hora. */
export const DiagnosticoForm: FC<DiagnosticoFormProps> = ({
  atendimento,
  onAtualizar,
  onFechar,
  onSituacaoChange,
}) => {
  const { t } = useT()
  const naTela = !onFechar
  const [gravandoSituacao, setGravandoSituacao] = useState(false)

  const cids = atendimento?.cids ?? []
  const hasCids = cids.length > 0

  const handleDelete = async (id: number) => {
    try {
      await deleteCid(atendimento?.id, id)
      onAtualizar()
    } catch (error) {
      showError(error)
    }
  }

  const formik = useFormik({
    initialValues: {
      situacaoDiagnostico: undefined as IOption<SituacaoDiagnosticoType> | undefined,
      cid: undefined as IOption<string> | undefined,
    },
    validationSchema: CIDSchema,
    onSubmit: async (values, { setSubmitting, setFieldValue }) => {
      setSubmitting(true)
      try {
        if (values.situacaoDiagnostico?.value === SituacaoDiagnosticoType.Diagnosticado) {
          const data: AtendimentoDtoCid = {
            codigo: values.cid?.value as string,
          }
          await adicionarCid(Number(atendimento?.id), data)
          setFieldValue('cid', undefined)
          onAtualizar()
        } else {
          await updateAtendimento(Number(atendimento?.id), {
            situacaoDiagnostico: values.situacaoDiagnostico?.value,
          })
          onAtualizar()
          onFechar?.()
        }
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  // `setFieldValue` é `useEventCallback` no formik: identidade estável entre renders.
  const { setFieldValue } = formik

  useEffect(() => {
    const situacaoInicial =
      atendimento?.situacaoDiagnostico ??
      (hasCids ? SituacaoDiagnosticoType.Diagnosticado : undefined)
    // Sem situação gravada (ex.: o backend a apaga junto com o último CID), o campo volta a vazio:
    // senão continuaria mostrando "Diagnosticado" sem CID nenhum.
    setFieldValue(
      'situacaoDiagnostico',
      situacaoInicial
        ? { label: situacaoDiagnosticoLabels[situacaoInicial], value: situacaoInicial }
        : undefined
    )
  }, [atendimento?.situacaoDiagnostico, hasCids, setFieldValue])

  const situacaoEscolhida = formik.values.situacaoDiagnostico?.value
  useEffect(() => {
    onSituacaoChange?.(situacaoEscolhida)
  }, [situacaoEscolhida, onSituacaoChange])

  /**
   * Na tela não há "Salvar situação": situação sem CID é gravada na escolha. "Diagnosticado" não se
   * grava sozinho (o backend exige CID); ele vem com o primeiro CID. O select fica travado enquanto
   * grava, para duas escolhas seguidas não chegarem fora de ordem.
   */
  const gravarSituacao = async (escolha: IOption | IOption[] | null) => {
    const valor = (escolha as IOption<SituacaoDiagnosticoType> | null)?.value
    const gravada = atendimento?.situacaoDiagnostico
    if (!valor || valor === SituacaoDiagnosticoType.Diagnosticado || valor === gravada) return
    setGravandoSituacao(true)
    try {
      await updateAtendimento(Number(atendimento?.id), { situacaoDiagnostico: valor })
      onAtualizar()
    } catch (error) {
      showError(error)
      // Volta ao gravado: escolher a mesma opção de novo tenta outra vez.
      setFieldValue(
        'situacaoDiagnostico',
        gravada ? { label: situacaoDiagnosticoLabels[gravada], value: gravada } : undefined
      )
    } finally {
      setGravandoSituacao(false)
    }
  }

  const isDiagnosticado =
    formik.values.situacaoDiagnostico?.value === SituacaoDiagnosticoType.Diagnosticado

  return (
    <form noValidate onSubmit={formik.handleSubmit} className={styles.cidDialog}>
      <div className='mb-6'>
        {hasCids ? (
          <div className='d-flex flex-column gap-2'>
            <Label className='fw-bold fs-6 mb-0' isRequired>
              Situação diagnóstica
            </Label>
            <div
              className={`d-flex align-items-center justify-content-between rounded px-4 py-3 ${styles.lockedField}`}
            >
              <span className='d-flex align-items-center gap-2 fs-6'>
                <span
                  className='rounded-circle'
                  style={{ width: 7, height: 7, background: 'var(--cid-brand-solid)' }}
                />
                {formik.values.situacaoDiagnostico?.label}
              </span>
            </div>
            <span className={`fs-8 ${styles.supportText}`}>
              Definida pelos CIDs abaixo. Remova todos para trocar a situação.
            </span>
          </div>
        ) : (
          <FormSelect
            formik={formik}
            name='situacaoDiagnostico'
            label='Situação diagnóstica'
            isRequired
            isDisabled={gravandoSituacao}
            onChange={naTela ? gravarSituacao : undefined}
            fetchOptions={OptionsHelper.getOptionsSituacoesDiagnostico}
            placeholder='Selecione a situação diagnóstica'
          />
        )}
      </div>

      {isDiagnosticado && (
        <div className='mb-6'>
          <div className='d-flex align-items-center gap-2 mb-3'>
            <span className={`fw-bold fs-7 text-uppercase ${styles.sectionLabel}`}>
              CIDs vinculados
            </span>
            <span className={`badge ${styles.countBadge}`}>{cids.length}</span>
          </div>

          <div className={`p-3 ${styles.sunkenBlock}`}>
            <ItemList
              items={cids}
              itemKey={(c, index) => c.id ?? index}
              itemClassName={`d-flex align-items-center gap-3 px-3 py-2 ${styles.card}`}
              deleteAriaLabel='Remover CID'
              onDelete={(c) => handleDelete(Number(c.id))}
              emptyContent={
                <div className={`text-center fs-7 py-4 ${styles.supportText}`}>
                  Nenhum CID vinculado ainda. Busque abaixo para adicionar o primeiro.
                </div>
              }
              renderItem={(c) => (
                <>
                  <span className={`badge fs-8 ${styles.codeBadge}`}>{c.cid.codigo}</span>
                  <span className={`flex-grow-1 fs-6 ${styles.description}`}>
                    {c.cid.descricao}
                  </span>
                </>
              )}
            />
          </div>
        </div>
      )}

      {isDiagnosticado && (
        <FormSelect
          formik={formik}
          name='cid'
          label='Adicionar outro CID'
          fetchOptions={OptionsHelper.getOptionsCidsByCodigo}
          placeholder='Buscar por código ou nome da doença'
          isRequired
        />
      )}

      {naTela ? (
        isDiagnosticado && (
          <div className='d-flex justify-content-end pt-4'>
            <Button
              type={ButtonType.Submit}
              variant='secondary-outline'
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? <LoadingIndicator /> : 'Adicionar CID'}
            </Button>
          </div>
        )
      ) : (
        <div className='d-flex flex-center flex-row-fluid pt-12'>
          <Button
            type={ButtonType.Reset}
            className='btn-secondary me-3'
            onClick={onFechar}
            disabled={formik.isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
            {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
          </Button>
        </div>
      )}
    </form>
  )
}
